<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div v-if="!hidden" class="timeout">
    <svg :viewBox="`0 0 ${SVG_VIEW} ${SVG_VIEW}`" class="timeout__svg" aria-hidden="true">
      <defs v-if="hasTrackGradient || hasProgressGradient">
        <linearGradient
          v-if="hasTrackGradient"
          :id="trackGradId"
          gradientUnits="objectBoundingBox"
          :x1="trackGradEndpoints.x1"
          :y1="trackGradEndpoints.y1"
          :x2="trackGradEndpoints.x2"
          :y2="trackGradEndpoints.y2"
        >
          <stop
            v-for="(s, i) in trackGradStops"
            :key="i"
            :offset="`${s.position}%`"
            :stop-color="s.color"
          />
        </linearGradient>
        <linearGradient
          v-if="hasProgressGradient"
          :id="progressGradId"
          gradientUnits="userSpaceOnUse"
          :x1="progressGradEndpoints.x1"
          :y1="progressGradEndpoints.y1"
          :x2="progressGradEndpoints.x2"
          :y2="progressGradEndpoints.y2"
        >
          <stop
            v-for="(s, i) in progressGradStops"
            :key="i"
            :offset="`${s.position}%`"
            :stop-color="s.color"
          />
        </linearGradient>
      </defs>

      <circle
        :cx="CENTER"
        :cy="CENTER"
        :r="radius"
        :fill="overrideFillColor"
        :stroke="hasTrackGradient ? `url(#${trackGradId})` : overrideTrackColor"
        :stroke-width="strokeWidthVB"
      />

      <path
        v-if="progressLength > 0"
        :d="progressPath"
        fill="none"
        :stroke="hasProgressGradient ? `url(#${progressGradId})` : overrideProgressColor"
        :stroke-width="strokeWidthVB"
        stroke-linecap="butt"
      />

      <text
        :key="pulseRunId"
        class="timeout__number"
        :class="{ 'is-pulsing': pulseEnabled && pulseRunId > 0 }"
        :x="CENTER"
        :y="CENTER"
        text-anchor="middle"
        dominant-baseline="central"
      >
        {{ remaining }}
      </text>
    </svg>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue'
import { PipeDisplayMode, usePipeIfAvailable } from '@render/composables/usePipe'
import { useComponentProcessors } from '@render/composables/useComponentProcessors'
import { useEditorCommands } from '@render/composables/useEditorCommands'
import { getColorStyleData, getSizeStyleData } from '@render/composables/useStyleData'
import type { GradientField } from '@render/types/styles'
import type { TimeoutFields as Model } from './definition'

const fields = defineModel<Model>('fields', {
  required: true,
  default: (): Model => ({}),
})

const emit = defineEmits<{
  (e: 'onComplete'): void
  (e: 'onTick'): void
}>()

const pipe = usePipeIfAvailable()
const isEditor = computed(() => pipe?.currentMode.value === PipeDisplayMode.EDITOR)

// SVG geometry — fixed logical viewBox; CSS scales the rendered size.
const SVG_VIEW = 100
const CENTER = SVG_VIEW / 2

const strokeWidthVB = computed(() => {
  const raw = Number(fields.value.strokeWidth)
  return Number.isFinite(raw) && raw > 0 ? raw : 5
})
const radius = computed(() => CENTER - strokeWidthVB.value / 2)
const circumference = computed(() => 2 * Math.PI * radius.value)

const initialSeconds = computed(() => {
  const raw = Number(fields.value.durationSeconds)
  return Number.isFinite(raw) && raw >= 0 ? Math.floor(raw) : 0
})

// Wall-clock-based countdown — `startTimestamp` is the Date.now() of the
// current run (null while paused / stopped / reset); `durationAtStart` is the
// seconds value the current run was started with. `remaining` is recomputed
// from those on each tick rather than decremented, so inactive-tab
// setInterval throttling can't accumulate drift — a delayed tick reads the
// correct elapsed time in one go. Mirrors the pattern in Timer.vue.
const startTimestamp = ref<number | null>(null)
const durationAtStart = ref<number>(0)
const remaining = ref<number>(0)

let intervalId: ReturnType<typeof setInterval> | null = null
let rafId: number | null = null

// Smooth (frame-driven) elapsed fraction in [0, 1]. The integer countdown
// (`remaining`) updates once per second, but when smoothProgress is on the
// arc and gradient endpoints read this rAF-driven value so the ring advances
// continuously instead of stepping on the second boundary.
const smoothElapsed = ref(0)

const pulseRunId = ref(0)
const pulseEnabled = computed(() => fields.value.pulseEnabled === true)
const pulseScale = computed(() => {
  const v = Number(fields.value.pulseScale)
  return Number.isFinite(v) && v > 1 ? v : 1.15
})
const pulseDurationMs = computed(() => {
  const v = Number(fields.value.pulseDuration)
  return Number.isFinite(v) && v >= 0 ? v : 400
})

function computeRemaining(): number {
  if (startTimestamp.value === null) return durationAtStart.value
  const elapsedSec = Math.floor((Date.now() - startTimestamp.value) / 1000)
  return Math.max(0, durationAtStart.value - elapsedSec)
}

function writeVariable(secs: number) {
  const key = fields.value.valueVariable?.key
  if (!key) return
  pipe?.setData(`var.${key}`, secs)
}

function tick() {
  const prev = remaining.value
  const next = computeRemaining()
  remaining.value = next
  writeVariable(next)

  if (next !== prev) {
    emit('onTick')
    if (pulseEnabled.value) pulseRunId.value++
  }

  if (prev > 0 && next === 0) {
    emit('onComplete')
    if (fields.value.loop) {
      durationAtStart.value = initialSeconds.value
      startTimestamp.value = Date.now()
      remaining.value = durationAtStart.value
      smoothElapsed.value = 0
      writeVariable(remaining.value)
      startSmoothLoop()
    } else {
      stopInterval()
      stopSmoothLoop()
      startTimestamp.value = null
    }
  }
}

function startInterval() {
  if (intervalId !== null) return
  intervalId = setInterval(tick, 1000)
}

function stopInterval() {
  if (intervalId !== null) {
    clearInterval(intervalId)
    intervalId = null
  }
}

function smoothTick() {
  if (startTimestamp.value === null || durationAtStart.value <= 0) {
    rafId = null
    return
  }
  const elapsedMs = Date.now() - startTimestamp.value
  const fraction = Math.max(0, Math.min(1, elapsedMs / (durationAtStart.value * 1000)))
  smoothElapsed.value = fraction
  if (fraction >= 1) {
    rafId = null
    return
  }
  rafId = requestAnimationFrame(smoothTick)
}

function startSmoothLoop() {
  if (fields.value.smoothProgress === false) return
  if (startTimestamp.value === null) return
  if (rafId !== null) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
  rafId = requestAnimationFrame(smoothTick)
}

function stopSmoothLoop() {
  if (rafId !== null) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
}

function startTimer(overrideSeconds?: unknown) {
  const override = Number(overrideSeconds)
  const baseDuration =
    Number.isFinite(override) && override >= 0
      ? Math.floor(override)
      : remaining.value > 0
        ? remaining.value
        : initialSeconds.value

  durationAtStart.value = baseDuration
  remaining.value = baseDuration
  smoothElapsed.value = 0
  writeVariable(baseDuration)

  if (baseDuration === 0) {
    startTimestamp.value = null
    stopInterval()
    stopSmoothLoop()
    emit('onComplete')
    return
  }

  startTimestamp.value = Date.now()
  startInterval()
  startSmoothLoop()
}

function stopTimer() {
  durationAtStart.value = remaining.value
  startTimestamp.value = null
  stopInterval()
  stopSmoothLoop()
}

function resetTimer() {
  stopInterval()
  stopSmoothLoop()
  startTimestamp.value = null
  durationAtStart.value = initialSeconds.value
  remaining.value = initialSeconds.value
  smoothElapsed.value = 0
  writeVariable(remaining.value)
}

function restartTimer() {
  resetTimer()
  startTimer()
}

useComponentProcessors({
  start: ({ config }) => startTimer(config?.override),
  stop: () => stopTimer(),
  reset: () => resetTimer(),
  restart: () => restartTimer(),
})

useEditorCommands({
  previewStart: () => startTimer(),
  previewReset: () => resetTimer(),
})

function onVisibilityChange() {
  if (typeof document === 'undefined') return
  if (document.visibilityState === 'visible' && startTimestamp.value !== null) {
    tick()
  }
}

onMounted(() => {
  remaining.value = initialSeconds.value
  durationAtStart.value = initialSeconds.value
  writeVariable(remaining.value)

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange)
  }

  if (!isEditor.value && fields.value.startOnLoad !== false) {
    startTimer()
  }
})

onBeforeUnmount(() => {
  stopInterval()
  stopSmoothLoop()
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }
})

watch(isEditor, (nowEditor) => {
  if (!nowEditor && fields.value.startOnLoad !== false && startTimestamp.value === null) {
    startTimer()
  }
})

watch(
  () => fields.value.valueVariable?.key,
  () => writeVariable(remaining.value),
)

watch(initialSeconds, (next) => {
  if (startTimestamp.value === null) {
    durationAtStart.value = next
    remaining.value = next
    writeVariable(remaining.value)
  }
})

watch(
  () => fields.value.smoothProgress,
  (smooth) => {
    if (smooth === false) {
      stopSmoothLoop()
    } else if (startTimestamp.value !== null) {
      startSmoothLoop()
    }
  },
)

// Progress geometry — clockwise from 12 o'clock, drawn as an SVG <path> so
// direction and gradient anchoring are unambiguous (browsers' implicit
// <circle> path direction is implementation-specific).
//
// While the timer is running with smoothProgress on, the arc reads the rAF-
// driven elapsed fraction so it advances continuously. When stopped, paused,
// or smoothProgress is off, it falls back to the integer-second stepped
// fraction derived from `remaining`, which keeps the arc accurate after a
// reset / stop / variable-key change without depending on the rAF clock.
//
// Editor idle state (in editor and not running a preview): the arc is
// pinned to 60% elapsed so authors can verify stroke styling — colours,
// widths, gradient direction along the progress edge — without having to
// click "Preview: Start". Once a preview is running (`startTimestamp` set),
// the live fraction takes over.
const EDITOR_IDLE_PREVIEW_FRACTION = 0.6
const progressFraction = computed(() => {
  let elapsed: number
  if (isEditor.value && startTimestamp.value === null) {
    elapsed = EDITOR_IDLE_PREVIEW_FRACTION
  } else {
    const total = durationAtStart.value
    if (total <= 0) return 0
    const useSmooth = fields.value.smoothProgress !== false && startTimestamp.value !== null
    elapsed = useSmooth ? smoothElapsed.value : 1 - remaining.value / total
  }
  return fields.value.fillDirection === 'full_to_empty' ? 1 - elapsed : elapsed
})

const progressLength = computed(() => progressFraction.value * circumference.value)

const progressEndPoint = computed(() => {
  const θ = progressFraction.value * 2 * Math.PI
  return {
    x: CENTER + radius.value * Math.sin(θ),
    y: CENTER - radius.value * Math.cos(θ),
  }
})

const progressPath = computed(() => {
  const r = radius.value
  const startX = CENTER
  const startY = CENTER - r
  const f = progressFraction.value
  if (f <= 0) return ''
  if (f >= 0.9999) {
    // Full circle — a single arc with equal start/end is degenerate, so split
    // into two semicircles.
    return `M ${startX} ${startY} A ${r} ${r} 0 1 1 ${startX} ${CENTER + r} A ${r} ${r} 0 1 1 ${startX} ${startY}`
  }
  const { x: endX, y: endY } = progressEndPoint.value
  const largeArc = f > 0.5 ? 1 : 0
  return `M ${startX} ${startY} A ${r} ${r} 0 ${largeArc} 1 ${endX} ${endY}`
})

// Style overrides — defaults match the source SVG exactly via stylesheet
// tokens (color-tertiary-200 ≈ #BDE5DF, color-secondary-600 = #03CEA4,
// color-primary-800 = #0B3E53, color-white = #FFFFFF).
const overrideFillColor = computed(
  () => getColorStyleData(fields.value.fillColor, 'var(--color-white)') ?? 'var(--color-white)',
)
const overrideTrackColor = computed(
  () =>
    getColorStyleData(fields.value.incompleteStrokeColor, 'var(--color-tertiary-200)') ??
    'var(--color-tertiary-200)',
)
const overrideProgressColor = computed(
  () =>
    getColorStyleData(fields.value.completeStrokeColor, 'var(--color-secondary-600)') ??
    'var(--color-secondary-600)',
)
const overrideTextColor = computed(
  () =>
    getColorStyleData(fields.value.textColor, 'var(--color-primary-800)') ??
    'var(--color-primary-800)',
)
const overrideSize = computed(() => getSizeStyleData(fields.value.size, '63px') ?? '63px')
const overrideTextSize = computed(
  () => getSizeStyleData(fields.value.textSize, 'var(--text-heading-2-size)') ?? '32px',
)
const overrideTextFontWeight = computed(() => {
  const w = fields.value.textFontWeight
  return w !== undefined && w !== null && w !== '' ? String(w) : 'var(--text-heading-2-weight)'
})

const pulseScaleCss = computed(() => String(pulseScale.value))
const pulseDurationCss = computed(() => `${pulseDurationMs.value}ms`)

// Per-instance gradient ids so multiple Timeouts on a page don't share paint
// servers — same rationale as HappyUserBadge. useId() is SSR-safe so the
// paint server reference hydrates correctly.
const uid = useId()
const trackGradId = `tmo-track-grad-${uid}`
const progressGradId = `tmo-progress-grad-${uid}`

type ResolvedStop = { position: number; color: string }

function resolveStops(g: GradientField | undefined): ResolvedStop[] | null {
  if (!g || g.type !== 'linear_gradient' || !g.stops || g.stops.length < 2) return null
  return [...g.stops]
    .sort((a, b) => a.position - b.position)
    .map((s) => ({
      position: s.position,
      color: getColorStyleData(s.color) ?? 'transparent',
    }))
}

const trackGradStops = computed(() => resolveStops(fields.value.incompleteStrokeGradient))
const progressGradStops = computed(() => resolveStops(fields.value.completeStrokeGradient))

const hasTrackGradient = computed(() => trackGradStops.value !== null)
const hasProgressGradient = computed(() => progressGradStops.value !== null)

// Track gradient endpoints — CSS-style angle interpreted over the element's
// bounding box. angle 0 = bottom→top, 90 = left→right, 180 = top→bottom.
const trackGradEndpoints = computed(() => {
  const angle = Number(fields.value.incompleteStrokeGradient?.angle ?? 180)
  const θ = (angle * Math.PI) / 180
  const sin = Math.sin(θ)
  const cos = Math.cos(θ)
  return {
    x1: 0.5 - 0.5 * sin,
    y1: 0.5 + 0.5 * cos,
    x2: 0.5 + 0.5 * sin,
    y2: 0.5 - 0.5 * cos,
  }
})

// Progress gradient endpoints — line from arc start (12 o'clock) to current
// arc tip in viewBox coordinates so the gradient runs along the progress
// edge. When the arc is empty or full the chord collapses; nudge the end
// slightly so the gradient still resolves to the first/last stop.
const progressGradEndpoints = computed(() => {
  const r = radius.value
  const startX = CENTER
  const startY = CENTER - r
  const f = progressFraction.value
  if (f <= 0.0001) {
    return { x1: startX, y1: startY, x2: startX + 0.001, y2: startY }
  }
  if (f >= 0.9999) {
    return { x1: startX, y1: startY, x2: startX, y2: startY + 0.001 }
  }
  const { x: endX, y: endY } = progressEndPoint.value
  return { x1: startX, y1: startY, x2: endX, y2: endY }
})

// Hide-on-zero — kept rendered in editor mode so the component stays visible
// on the canvas, and suppressed when looping (the cycle restarts immediately).
const hidden = computed(
  () =>
    fields.value.hideOnZero !== false &&
    !fields.value.loop &&
    !isEditor.value &&
    durationAtStart.value > 0 &&
    remaining.value === 0 &&
    startTimestamp.value === null,
)
</script>

<style scoped lang="scss">
.timeout {
  display: inline-block;
  width: v-bind(overrideSize);
  height: v-bind(overrideSize);
  line-height: 0;
}

.timeout__svg {
  width: 100%;
  height: 100%;
  display: block;
}

.timeout__number {
  fill: v-bind(overrideTextColor);
  font-size: v-bind(overrideTextSize);
  font-weight: v-bind(overrideTextFontWeight);
  font-family: var(--text-heading-2-family);
  user-select: none;
  transform-box: fill-box;
  transform-origin: center;
}

.timeout__number.is-pulsing {
  animation-name: tmo-pulse;
  animation-duration: v-bind(pulseDurationCss);
  animation-timing-function: ease-out;
  animation-fill-mode: both;
  --tmo-pulse-scale: v-bind(pulseScaleCss);
}

@keyframes tmo-pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(var(--tmo-pulse-scale));
  }
  100% {
    transform: scale(1);
  }
}
</style>
